
/**
 * Schema.org Structured Data Utilities
 * 
 * Minimal client-side schema helpers.
 * All critical JSON-LD (FAQPage, WebPage, BreadcrumbList, LocalBusiness, Article, etc.)
 * are generated exclusively in entry-server-conditional.tsx (SSR-only).
 * 
 * This file retains only functions still imported by client components:
 * - createOrganizationSchema (PageSEO.tsx)
 * - createWebsiteSchema (PageSEO.tsx)
 * - combineSchemas (PageSEO.tsx)
 * - createPriceProductSchema (Index.tsx)
 */

// Base types for Schema.org entities
interface SchemaBase {
  '@context': string;
  '@type': string;
  [key: string]: any; // Allow any additional properties
}

interface SchemaOrganization extends SchemaBase {
  '@type': 'Organization';
  name: string;
  url: string;
  logo: {
    '@type': 'ImageObject';
    url: string;
    width?: number;
    height?: number;
  };
  sameAs?: string[];
}

// Website schema
export const createWebsiteSchema = (): SchemaBase => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://www.gildy.it/#website',
  name: 'Gildy',
  url: 'https://www.gildy.it',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.gildy.it/search?q={search_term_string}',
    'query-input': 'required name=search_term_string'
  }
});

// Organization schema
export const createOrganizationSchema = (): SchemaOrganization => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': 'https://www.gildy.it/#organization',
  name: 'Gildy',
  url: 'https://www.gildy.it',
  logo: {
    '@type': 'ImageObject',
    url: 'https://www.gildy.it/logo.png',
    width: 112,
    height: 112
  },
  sameAs: [
    'https://www.facebook.com/gildyit',
    'https://www.instagram.com/gildy_it',
    'https://twitter.com/gildy_it',
    'https://www.linkedin.com/company/gildy'
  ]
});

// Product schema for gold/silver prices
export interface PriceProductSchemaProps {
  name: string;
  description: string;
  image: string;
  price: number;
  priceCurrency: string;
  url: string;
}

export const createPriceProductSchema = (props: PriceProductSchemaProps): SchemaBase => {
  const { name, description, image, price, priceCurrency, url } = props;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image,
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency,
      availability: 'https://schema.org/InStock',
      url
    }
  };
};

// Combine multiple schema objects into a single script
export const combineSchemas = (...schemas: SchemaBase[]): string => {
  if (schemas.length === 1) {
    return JSON.stringify(schemas[0]);
  }

  // For multiple schemas, use @graph
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': schemas
  });
};
